import { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Building2, Check, ChevronsUpDown, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkgroups } from '@/hooks/useWorkgroups';
import type { WorkgroupMembership } from '@/api/auth';

interface WorkgroupSwitcherProps {
  className?: string;
}

const getWorkgroupLabel = (workgroup: WorkgroupMembership): string => {
  return workgroup.title;
};

export function WorkgroupSwitcher({ className }: WorkgroupSwitcherProps) {
  const { workgroups, activeWorkgroupId, activeWorkgroup, switchWorkgroup } = useWorkgroups();
  const [isOpen, setIsOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<number | null>(null);

  const isSwitching = switchingId !== null;

  const handleSelect = async (workgroupId: number) => {
    if (workgroupId === activeWorkgroupId || isSwitching) return;

    setSwitchingId(workgroupId);
    try {
      await switchWorkgroup(workgroupId);
    } catch (error) {
      toast.error('Failed to switch workgroup. Please try again.');
    } finally {
      setSwitchingId(null);
    }
  };

  if (workgroups.length === 0) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`flex items-center gap-2 bg-background hover:bg-accent rounded-lg shadow-sm max-w-[180px] ${className || ''}`}
          disabled={isSwitching}
        >
          {isSwitching ? (
            <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
          ) : activeWorkgroup?.workgrouptype === 'PERSONAL' ? (
            <User className="w-4 h-4 shrink-0" />
          ) : (
            <Building2 className="w-4 h-4 shrink-0" />
          )}
          <span className="truncate text-sm font-medium">
            {activeWorkgroup ? getWorkgroupLabel(activeWorkgroup) : 'Select workgroup'}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-64 bg-popover shadow-lg rounded-lg border border-border" align="end">
        <DropdownMenuLabel>Workgroups</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workgroups.map((workgroup) => {
          const isActive = workgroup.id === activeWorkgroupId;
          return (
            <DropdownMenuItem
              key={workgroup.id}
              onClick={() => handleSelect(workgroup.id)}
              disabled={isSwitching}
              className="flex items-center gap-2"
            >
              {workgroup.workgrouptype === 'PERSONAL' ? (
                <User className="w-4 h-4 shrink-0 text-muted-foreground" />
              ) : (
                <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate text-sm">{getWorkgroupLabel(workgroup)}</span>
                <span className="truncate text-xs text-muted-foreground">{workgroup.role}</span>
              </div>
              {switchingId === workgroup.id ? (
                <Loader2 className="w-4 h-4 shrink-0 animate-spin text-muted-foreground" />
              ) : isActive ? (
                <Check className="w-4 h-4 shrink-0 text-primary" />
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default WorkgroupSwitcher;
